import { DataGridPro, GridSortModel } from '@mui/x-data-grid-pro';
import { GridCellParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import {
  Permission,
  PermissionAction,
  PermissionResource,
  PermissionScope,
} from '../../../data/models/permission.model';
import { IconButton, Paper, styled, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import securityUtils from '../../../services/securityUtils';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'PermissionDataTable';

export const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
};

const DataTableContainer = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    justifyContent: 'stretch',
    minWidth: 0,
  },

  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
  },
}));

interface PermissionDataTableProps {
  permissions: Permission[];
  resources: PermissionResource[];
  actions: PermissionAction[];
  scopes: PermissionScope[];
  loading: boolean;
  readOnly: boolean;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onSortModelChange: (model: GridSortModel) => void;
  sortModel: GridSortModel;
}

export default function PermissionDataTable({
  permissions,
  resources,
  actions,
  scopes,
  loading,
  readOnly,
  onEdit,
  onDelete,
  onSortModelChange,
  sortModel,
}: PermissionDataTableProps) {
  const { t } = useTranslation('common');
  const isSuperReaderUser = useIsSuperReaderUser();

  const renderPermissionName = (params: GridCellParams) => {
    return securityUtils.getPermissionName(
      params.row,
      resources,
      actions,
      scopes,
    );
  };

  const columns = [
    {
      field: 'userPermissionName',
      headerName: t('permissions.permissionName'),
      flex: 1,
      renderCell: renderPermissionName,
    },
    {
      field: 'description',
      headerName: t('permissions.description'),
      flex: 2,
    },
    {
      field: 'Actions',
      headerName: ' ',
      width: 120,
      hide: readOnly,
      renderCell: (params: GridCellParams) => {
        return (
          <>
            {!isSuperReaderUser && (
              <Tooltip title={t('permissions.editPermission')}>
                <IconButton
                  data-testid="edit"
                  onClick={() => onEdit(params.row.userPermissionID)}
                  size="large">
                  <EditIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {!isSuperReaderUser && (
              <Tooltip title={t('permissions.deletePermission')}>
                <IconButton
                  data-testid="edit"
                  onClick={() => onDelete(params.row.userPermissionID)}
                  size="large">
                  <DeleteForeverIcon color="error" />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
      sortable: false,
    },
  ];

  return (
    <DataTableContainer data-testid="role-table" className={classes.container}>
      <DataGridPro
        getRowId={(row) => row.userPermissionID!}
        rows={permissions}
        columns={columns}
        rowCount={permissions.length}
        autoHeight={true}
        loading={loading}
        className={classes.grid}
        checkboxSelection={false}
        disableRowSelectionOnClick={true}
        disableColumnMenu={true}
        disableColumnSelector={true}
        hideFooter={true}
        sortingMode="server"
        initialState={{
          sorting: {
            sortModel: [{ field: 'description', sort: 'asc' }],
          },
        }}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        sortingOrder={['asc', 'desc']}
      />
    </DataTableContainer>
  );
}
